import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Camera, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { documentsAPI } from '../../lib/api';
import { stationNetworkApi } from '../../lib/stationNetworkApi';
import { messageErreur } from '../../lib/erreurs';
import { optionsLibelles } from '../../lib/libelles';
import { formatDate } from '../../lib/formats';
import { Aide } from '../ui/aide';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { StatutBadge } from '../ui/StatutBadge';
import { SearchableSelect } from '../ui/searchable-select';
import { LoadingState } from '../ui/loading-state';
import { EmptyState } from '../ui/empty-state';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

/**
 * Registre des pompes d'une station (lot 034) : étalonnage, scellés,
 * inspections (avec photos), pannes. Une ligne par pompe (dernier étalonnage,
 * scellés posés, dernière inspection, prochaine échéance et son état), puis
 * l'historique. Un événement est un fait daté : il ne se corrige pas, on en
 * ajoute un autre.
 */

const NATURES = ['CALIBRATION', 'SEAL', 'INSPECTION', 'BREAKDOWN'];

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground [&_tr:last-child_td]:border-0';

const aujourdhui = () => {
  const date = new Date();
  const decalage = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - decalage).toISOString().slice(0, 10);
};

const Champ = ({ id, label, children }) => (
  <div className="grid gap-1.5">
    <label htmlFor={id} className="text-sm font-medium text-foreground">
      {label}
    </label>
    {children}
  </div>
);

const Panneau = ({ titre, children }) => (
  <section className="rounded-xl border border-border bg-card shadow-sm">
    <header className="px-4 pb-2 pt-3">
      <h2 className="text-base font-semibold">{titre}</h2>
    </header>
    <div className="overflow-x-auto px-1 pb-3">{children}</div>
  </section>
);

const EVENEMENT_VIDE = {
  pump_id: '',
  nozzle_id: '',
  kind: 'CALIBRATION',
  event_date: '',
  seal_number: '',
  next_due_date: '',
  performed_by: '',
  notes: '',
};

const EvenementDialog = ({ open, onOpenChange, stationId, pompes, pompeInitiale, onSaved }) => {
  const [form, setForm] = useState(EVENEMENT_VIDE);
  const [photos, setPhotos] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EVENEMENT_VIDE, pump_id: pompeInitiale || pompes[0]?.pump_id || '', event_date: aujourdhui() });
    setPhotos([]);
  }, [open, pompeInitiale, pompes]);

  const changer = (cle) => (event) => setForm((avant) => ({ ...avant, [cle]: event?.target ? event.target.value : event }));
  const pompe = pompes.find((ligne) => ligne.pump_id === form.pump_id);
  const optionsPompes = useMemo(() => pompes.map((ligne) => ({ value: ligne.pump_id, label: ligne.name })), [pompes]);
  const optionsPistolets = useMemo(
    () => [
      { value: '', label: 'Toute la pompe' },
      ...(pompe?.nozzles || []).map((pistolet) => ({
        value: pistolet.id,
        label: pistolet.label || `Pistolet ${pistolet.number}`,
      })),
    ],
    [pompe],
  );
  const avecEcheance = form.kind === 'CALIBRATION' || form.kind === 'INSPECTION';

  const ajouterPhotos = async (event) => {
    const fichiers = Array.from(event.target.files || []);
    event.target.value = '';
    if (!fichiers.length) return;
    setEnvoi(true);
    try {
      const envoyes = [];
      for (const fichier of fichiers) {
        const document = await documentsAPI.upload(fichier, 'OTHER');
        envoyes.push({ document_id: document.id, name: fichier.name });
      }
      setPhotos((avant) => [...avant, ...envoyes].slice(0, 6));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’envoyer la photo'));
    } finally {
      setEnvoi(false);
    }
  };

  const enregistrer = async (event) => {
    event.preventDefault();
    setEnCours(true);
    try {
      await stationNetworkApi.pumpRegistry.createEvent(stationId, {
        pump_id: form.pump_id,
        nozzle_id: form.nozzle_id || undefined,
        kind: form.kind,
        event_date: form.event_date || undefined,
        seal_number: form.kind === 'SEAL' ? form.seal_number.trim() : undefined,
        next_due_date: avecEcheance && form.next_due_date ? form.next_due_date : undefined,
        performed_by: form.performed_by.trim() || undefined,
        notes: form.notes.trim() || undefined,
        photos: photos.length ? photos : undefined,
      });
      toast.success('Événement ajouté au registre');
      onSaved();
      onOpenChange(false);
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible d’ajouter l’événement'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            Nouvel événement
            <Aide titre="Registre des pompes">
              Chaque événement est un fait daté : il ne se corrige pas. Pour un étalonnage ou une inspection, indiquez la
              prochaine échéance.
            </Aide>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={enregistrer} className="grid gap-4 sm:grid-cols-2">
          <Champ id="evenement-pompe" label="Pompe">
            <SearchableSelect id="evenement-pompe" value={form.pump_id} onValueChange={changer('pump_id')} options={optionsPompes} aria-label="Pompe" placeholder="Choisir la pompe" />
          </Champ>
          <Champ id="evenement-pistolet" label="Pistolet">
            <SearchableSelect id="evenement-pistolet" value={form.nozzle_id} onValueChange={changer('nozzle_id')} options={optionsPistolets} aria-label="Pistolet" />
          </Champ>
          <Champ id="evenement-nature" label="Nature">
            <SearchableSelect id="evenement-nature" value={form.kind} onValueChange={changer('kind')} options={optionsLibelles('station_pompe_evenement', NATURES)} aria-label="Nature" />
          </Champ>
          <Champ id="evenement-date" label="Date">
            <Input id="evenement-date" type="date" max={aujourdhui()} value={form.event_date} onChange={changer('event_date')} required />
          </Champ>
          {form.kind === 'SEAL' && (
            <Champ id="evenement-scelles" label="Numéro des scellés">
              <Input id="evenement-scelles" value={form.seal_number} onChange={changer('seal_number')} maxLength={60} required />
            </Champ>
          )}
          {avecEcheance && (
            <Champ id="evenement-echeance" label="Prochaine échéance">
              <Input id="evenement-echeance" type="date" min={form.event_date || undefined} value={form.next_due_date} onChange={changer('next_due_date')} />
            </Champ>
          )}
          <Champ id="evenement-intervenant" label="Réalisé par">
            <Input id="evenement-intervenant" value={form.performed_by} onChange={changer('performed_by')} maxLength={120} />
          </Champ>
          <div className="sm:col-span-2">
            <Champ id="evenement-notes" label="Notes">
              <Input id="evenement-notes" value={form.notes} onChange={changer('notes')} maxLength={1000} />
            </Champ>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <label
              htmlFor="evenement-photos"
              className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-dashed border-primary px-3 text-sm font-semibold text-primary"
            >
              <Camera className="h-4 w-4" aria-hidden="true" />
              {envoi ? 'Envoi…' : 'Ajouter des photos'}
            </label>
            <input id="evenement-photos" type="file" accept="image/*" multiple className="sr-only" onChange={ajouterPhotos} disabled={envoi || photos.length >= 6} />
            {photos.map((photo) => (
              <span key={photo.document_id} className="rounded-md bg-muted px-2 py-1 text-xs">
                {photo.name}
              </span>
            ))}
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || envoi || !form.pump_id || (form.kind === 'SEAL' && !form.seal_number.trim())}>
              Ajouter
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PumpRegistry = ({ station, peutEcrire, onChanged }) => {
  const stationId = station?.id;
  const [registre, setRegistre] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [dialogue, setDialogue] = useState({ ouvert: false, pompe: null });

  const charger = useCallback(async () => {
    if (!stationId) return;
    setChargement(true);
    try {
      setRegistre(await stationNetworkApi.pumpRegistry.get(stationId));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de charger le registre des pompes'));
      setRegistre(null);
    } finally {
      setChargement(false);
    }
  }, [stationId]);

  useEffect(() => {
    charger();
  }, [charger]);

  const apresAjout = async () => {
    await charger();
    onChanged?.();
  };

  if (chargement && !registre) return <LoadingState shape="table" />;
  if (!registre) return null;
  if (registre.pumps.length === 0) return <EmptyState variant="empty" title="Aucune pompe" className="border-0" />;

  return (
    <div className="grid gap-4">
      <Panneau titre="Pompes">
        <table className={CELLULES}>
          <thead>
            <tr>
              <th className="text-left">Pompe</th>
              <th className="text-left">Dernier étalonnage</th>
              <th className="text-left">Scellés posés</th>
              <th className="text-left">Dernière inspection</th>
              <th className="text-left">Prochaine échéance</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {registre.pumps.map((pompe) => (
              <tr key={pompe.pump_id}>
                <td className="font-medium">
                  {pompe.name}
                  {!pompe.is_active && <span className="text-xs text-muted-foreground"> · hors service</span>}
                </td>
                <td className="whitespace-nowrap">{pompe.last_calibration ? formatDate(pompe.last_calibration.event_date) : '—'}</td>
                <td className="whitespace-nowrap">
                  {pompe.last_seal ? `${pompe.last_seal.seal_number} · ${formatDate(pompe.last_seal.event_date)}` : '—'}
                </td>
                <td className="whitespace-nowrap">{pompe.last_inspection ? formatDate(pompe.last_inspection.event_date) : '—'}</td>
                <td>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="whitespace-nowrap tabular-nums">{pompe.next_due_date ? formatDate(pompe.next_due_date) : '—'}</span>
                    {pompe.status !== 'NONE' && <StatutBadge domaine="station_echeance" code={pompe.status} />}
                  </div>
                </td>
                <td className="text-right">
                  {peutEcrire && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setDialogue({ ouvert: true, pompe: pompe.pump_id })}>
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Événement
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panneau>

      <Panneau titre="Historique">
        {registre.events.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">Aucun événement</p>
        ) : (
          <table className={CELLULES}>
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Pompe</th>
                <th className="text-left">Nature</th>
                <th className="text-left">Détail</th>
                <th className="text-left">Réalisé par</th>
                <th className="text-right">Photos</th>
              </tr>
            </thead>
            <tbody>
              {registre.events.map((evenement) => (
                <tr key={evenement.id}>
                  <td className="whitespace-nowrap">{formatDate(evenement.event_date)}</td>
                  <td>
                    {evenement.pump_name}
                    {evenement.nozzle_number ? ` · pistolet ${evenement.nozzle_number}` : ''}
                  </td>
                  <td>
                    <StatutBadge domaine="station_pompe_evenement" code={evenement.kind} />
                  </td>
                  <td>
                    {[
                      evenement.seal_number && `Scellés ${evenement.seal_number}`,
                      evenement.next_due_date && `Échéance ${formatDate(evenement.next_due_date)}`,
                      evenement.notes,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                  <td>{evenement.performed_by || '—'}</td>
                  <td className="text-right tabular-nums">{evenement.photos?.length || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panneau>

      {stationId && (
        <EvenementDialog
          open={dialogue.ouvert}
          onOpenChange={(ouvert) => !ouvert && setDialogue({ ouvert: false, pompe: null })}
          stationId={stationId}
          pompes={registre.pumps}
          pompeInitiale={dialogue.pompe}
          onSaved={apresAjout}
        />
      )}
    </div>
  );
};

export default PumpRegistry;
